import { Injectable } from '@angular/core';

import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, repeat } from 'rxjs/operators';

import { ConditionService } from './condition.service';
import { VisibilityService } from './visibility.service';

/**
 * Service that handle field visibility/hidding
 */
@Injectable()
export class HiddenService {
  private readonly repeatSubject = new Subject<string[]>();

  constructor(
    private readonly conditionService: ConditionService,
    private readonly visibilityService: VisibilityService,
  ) {}

  private isHiddenByMeta(names: string[]): boolean {
    return this.conditionService.isHidden(names);
  }

  private isHiddenByMeta$(names: string[]): Observable<boolean> {
    return this.conditionService.isHidden$(names);
  }

  private isHiddenByVisibilityFunction(names: string[]): boolean {
    return this.visibilityService.isHidden(names);
  }

  private isHiddenByVisibilityFunction$(names: string[]): Observable<boolean> {
    return this.visibilityService.isHidden$(names);
  }

  /**
   * Check that page/field/control by name should be hidden. By condition, metadata, visibility function or
   * by form mode (remove in https://gitlab-dev.ispsystem.net/ispsystem5/isp6theme/-/merge_requests/653)
   *
   * @param names - page/field/control names
   */
  isHidden(...names: string[]): boolean {
    const hiddenByMeta = this.isHiddenByMeta(names);
    const hiddenByVisibilityFunction = this.isHiddenByVisibilityFunction(names);
    return hiddenByMeta || hiddenByVisibilityFunction;
  }

  /**
   * Stream of page/field/control by name hidding state. By condition, metadata, or by form mode
   *
   * @param names - page/field/control names
   */
  isHidden$(...names: string[]): Observable<boolean> {
    return combineLatest([
      this.isHiddenByMeta$(names),
      this.isHiddenByVisibilityFunction$(names),
    ]).pipe(
      map(([byMeta, byVisibilityFunction]) => byMeta || byVisibilityFunction),
      repeat({
        delay: () =>
          this.repeatSubject.pipe(
            filter(repeatNames => repeatNames.some(n => names.includes(n))),
          ),
      }),
    );
  }

  validateIsHidden(...names: string[]): void {
    this.repeatSubject.next(names);
  }
}
